import React, { useState } from 'react'
import { ChevronDown, ChevronRight, Plus, Wand2 } from 'lucide-react'
import Button from '../../ui/Button'
import CodeSheetStatusBanner from '../CodeSheetStatusBanner'
import type { AppendixConsistencyResult, AppendixFinding, UnselectableRuleLine } from '../../../types/codeIndex'
import {
  formatCodeReference,
  formatCombinationCount,
  formatDisagreementCount,
  formatSlotPattern,
} from '../../../utils/appendixConsistency'

export const APPENDIX_RULES_MATCH_MESSAGE = 'The rules permit exactly the combinations this table lists.'
export const APPENDIX_FINDINGS_INSTRUCTION = 'Change the rules or the table to resolve each one.'
export const APPENDIX_FINDINGS_RECHECK_INSTRUCTION = 'Save to recheck.'

type AppendixConsistencyBannerProps = {
  result: AppendixConsistencyResult
  // The table changed since the comparison ran; only a save can re-evaluate it.
  needsRecheck: boolean
}

/** Top of a marked appendix: how many places the rules and the table disagree, or that they match. */
export const AppendixConsistencyBanner: React.FC<AppendixConsistencyBannerProps> = ({ result, needsRecheck }) => {
  const count = result.findings.length
  if (count === 0) {
    return (
      <CodeSheetStatusBanner testId="appendix-consistency-banner" variant={result.complete ? 'success' : 'warning'}>
        {result.complete
          ? APPENDIX_RULES_MATCH_MESSAGE
          : 'No disagreement was found, but the comparison could not finish. Save again to recheck.'}
      </CodeSheetStatusBanner>
    )
  }

  return (
    <CodeSheetStatusBanner testId="appendix-consistency-banner" variant="error">
      <span className="font-semibold">{formatDisagreementCount(count)}</span>{' '}
      <span data-testid="appendix-consistency-instruction">
        {needsRecheck ? APPENDIX_FINDINGS_RECHECK_INSTRUCTION : APPENDIX_FINDINGS_INSTRUCTION}
      </span>
    </CodeSheetStatusBanner>
  )
}

const ruleLineKey = (line: UnselectableRuleLine, index: number) => `${line.ruleId ?? line.formatValueId}:${index}`

type RowFindingProps = {
  finding: AppendixFinding
  disabled?: boolean
  readOnly?: boolean
  onEditRule?: (line: UnselectableRuleLine) => void
  onRemoveRow: () => void
}

/** Under a row the rules reject: the rules that reject it, and the two ways out. */
export const AppendixRowFinding: React.FC<RowFindingProps> = ({ finding, disabled, readOnly, onEditRule, onRemoveRow }) => {
  const [headline, ...details] = finding.message.split('\n')
  return (
    <div role="note" data-testid="appendix-row-finding" className="mt-1 rounded border border-[#F3C2BE] bg-[#FFF5F5] px-3 py-2 text-xs text-[#B3261E]">
      <div className="font-semibold">{headline}</div>
      {details.map((line, index) => <div key={index} className="mt-0.5">{line}</div>)}
      {!readOnly ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {finding.rules.map((line, index) => (
            <Button
              key={ruleLineKey(line, index)}
              type="button"
              size="sm"
              variant="secondary"
              className="bg-white"
              disabled={disabled || !onEditRule}
              onClick={() => onEditRule?.(line)}
            >
              Edit rule on {formatCodeReference(line.codeItemCode)} value {line.optionCode}
            </Button>
          ))}
          <Button type="button" size="sm" variant="secondary" className="bg-white" disabled={disabled} onClick={onRemoveRow}>
            Remove row
          </Button>
        </div>
      ) : null}
    </div>
  )
}

type AppendixTableFindingsProps = {
  findings: AppendixFinding[]
  disabled?: boolean
  readOnly?: boolean
  onAddToTable: (finding: AppendixFinding) => void
  onProposeRule?: (finding: AppendixFinding) => void
  onGoToCodeItem?: (code: string) => void
}

/**
 * Below the rows: combinations the rules permit that the table leaves out, grouped by shape, plus
 * findings about the table as a whole. They have no row to attach to.
 */
export const AppendixTableFindings: React.FC<AppendixTableFindingsProps> = ({
  findings,
  disabled,
  readOnly,
  onAddToTable,
  onProposeRule,
  onGoToCodeItem,
}) => {
  if (findings.length === 0) return null

  return (
    <section aria-label="Combinations the table does not list" data-testid="appendix-table-findings">
      <h3 className="mb-2 text-sm font-semibold text-gray-900">Not in the table</h3>
      <ul className="space-y-2">
        {findings.map(finding => (
          <li key={finding.id}>
            <TableFinding
              finding={finding}
              disabled={disabled}
              readOnly={readOnly}
              onAddToTable={onAddToTable}
              onProposeRule={onProposeRule}
              onGoToCodeItem={onGoToCodeItem}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

const TableFinding: React.FC<Omit<AppendixTableFindingsProps, 'findings'> & { finding: AppendixFinding }> = ({
  finding,
  disabled,
  readOnly,
  onAddToTable,
  onProposeRule,
  onGoToCodeItem,
}) => {
  const [expanded, setExpanded] = useState(false)
  const listed = finding.combinations.length
  const truncated = listed < finding.combinationCount
  const isOmission = finding.kind === 'OmittedCombinations'
  const codeItems = Array.from(new Set(finding.pattern.map(slot => slot.codeItemCode)))

  const proposalReason = finding.proposal
    ? ''
    : 'No single rule can exclude these without making a value unselectable. Change the table, or edit the rules directly.'

  return (
    <div data-testid="appendix-table-finding" className="rounded border border-[#F3C2BE] bg-[#FFF5F5] px-3 py-2 text-xs text-[#B3261E]">
      <div className="font-semibold">{finding.message}</div>
      {finding.kind === 'OmittedCombinations' && finding.pattern.length > 0 ? (
        <div className="mt-0.5 text-gray-700">{formatSlotPattern(finding.pattern)}</div>
      ) : null}

      {isOmission && listed > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(current => !current)}
          aria-expanded={expanded}
          className="mt-1 inline-flex cursor-pointer items-center gap-1 text-[#1D5176] hover:underline"
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
          {expanded ? 'Hide' : 'Show'} {formatCombinationCount(finding.combinationCount)}
        </button>
      ) : null}
      {expanded ? (
        <ul className="mt-1 max-h-40 overflow-auto rounded bg-white px-2 py-1 font-mono text-[11px] text-gray-800" data-testid="appendix-finding-combinations">
          {finding.combinations.map((combination, index) => <li key={index}>{combination.join(' · ')}</li>)}
          {truncated ? <li className="italic text-gray-500">and {finding.combinationCount - listed} more</li> : null}
        </ul>
      ) : null}

      {!readOnly && isOmission ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <span title={proposalReason}>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="bg-white"
              disabled={disabled || !finding.proposal || !onProposeRule}
              onClick={() => onProposeRule?.(finding)}
              leftIcon={<Wand2 className="h-3.5 w-3.5" aria-hidden="true" />}
            >
              Add a rule excluding {finding.combinationCount === 1 ? 'it' : 'them'}
            </Button>
          </span>
          <span title={truncated ? `Only the first ${listed} combinations can be added here; add the rest by hand or exclude them by rule.` : ''}>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="bg-white"
              disabled={disabled || truncated || listed === 0}
              onClick={() => onAddToTable(finding)}
              leftIcon={<Plus className="h-3.5 w-3.5" aria-hidden="true" />}
            >
              Add {finding.combinationCount === 1 ? 'it' : 'them'} to the table
            </Button>
          </span>
        </div>
      ) : null}

      {!readOnly && finding.kind === 'NoRules' && onGoToCodeItem ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {codeItems.map(code => (
            <Button key={code} type="button" size="sm" variant="secondary" className="bg-white" disabled={disabled} onClick={() => onGoToCodeItem(code)}>
              Add rules on {formatCodeReference(code)}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
